import {LockClosedIcon, XIcon} from '@heroicons/react/outline';
import type {ReactElement} from 'react';
import {useCallback, useState} from 'react';

import {Button, PlusIcon, Select} from '@/components';
import {BusinessFeeComparator, BusinessFeeField} from '@/types';
import {formatSelectOptionItem} from '@/utils/merchant';
import {showErrorMessage} from '@/utils/showErrorMessage';

import {FeeRuleValueInput} from './FeeRuleValueInput';
import {getBusinessIdTargets} from './updateFeeReducer';
type Props = {
  onChange: (value: string) => void;
  tags?: string[];
  value?: string;
  onRemove: (tag: string) => void;
  fieldsToOmit: BusinessFeeField[];
  businessId?: string;
  /** Test hook prefix; the field/comparator/value widgets and Add name under it. */
  dataCy?: string;
};

const mapFieldToComparators = (
  field: BusinessFeeField,
): BusinessFeeComparator[] => {
  switch (field) {
    case BusinessFeeField.FromCurrency:
    case BusinessFeeField.ToCurrency:
    case BusinessFeeField.FromCountry:
    case BusinessFeeField.ToCountry:
    case BusinessFeeField.FromProcessor:
    case BusinessFeeField.ToProcessor:
    case BusinessFeeField.FromChannel:
    case BusinessFeeField.ToChannel:
    case BusinessFeeField.UserId:
    case BusinessFeeField.BusinessId:
    case BusinessFeeField.TransactionType:
    case BusinessFeeField.WalletTag:
      return [BusinessFeeComparator.Equals, BusinessFeeComparator.NotEquals];
    case BusinessFeeField.FromAmount:
    case BusinessFeeField.ToAmount:
    case BusinessFeeField.TransactionDate:
      return [
        BusinessFeeComparator.Equals,
        BusinessFeeComparator.LessThan,
        BusinessFeeComparator.GreaterThan,
        BusinessFeeComparator.Between,
      ];

    default:
      return [];
  }
};
const LABEL_OVERRIDES: Record<string, string> = {
  [BusinessFeeField.WalletTag]: 'Wallet Type',
};

const mapOptionsToList = (options: string[], fieldsToOmit: string[] = []) =>
  options
    .filter((item) => !fieldsToOmit.includes(item))
    .map((item) => ({
      key: LABEL_OVERRIDES[item] ?? formatSelectOptionItem(item),
      value: item,
    }));

export const FeePredicateInput = ({
  onChange,
  tags,
  onRemove,
  fieldsToOmit,
  businessId,
  dataCy,
}: Props): ReactElement => {
  // When scoped to a merchant, the enterprise association is fixed: hide the
  // BusinessId field from the dropdown and protect its tag from removal,
  // including a multi-target tag (BUSINESSID_EQUALS_a,b): removing it would
  // silently turn a shared fee into a global one.
  const isProtectedTag = (tag: string) =>
    getBusinessIdTargets(tag).some((id) => id === businessId);
  const predicateFieldOptions = mapOptionsToList(
    Object.values(BusinessFeeField),
    businessId ? [...fieldsToOmit, BusinessFeeField.BusinessId] : fieldsToOmit,
  );
  const [selectedField, setSelectedField] = useState<BusinessFeeField>(
    BusinessFeeField.FromCurrency,
  );
  const [selectedComparator, setSelectedComparator] =
    useState<BusinessFeeComparator>(BusinessFeeComparator.Equals);

  const [selectedValue, setSelectedValue] = useState<string>('');
  const [filteredComparators, setFilteredComparators] = useState<
    BusinessFeeComparator[]
  >(() => mapFieldToComparators(selectedField));

  const comparatorOptions = mapOptionsToList(filteredComparators);

  const handleFieldChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const field = event.target.value as BusinessFeeField;
    setSelectedField(field);
    const comparators = mapFieldToComparators(field);
    setFilteredComparators(comparators);
    setSelectedComparator(comparators[0]);
  };

  const handleComparatorChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setSelectedComparator(event.target.value as BusinessFeeComparator);
  };

  const handleValueChange = (val: string) => {
    if (val) {
      setSelectedValue(val);
    }
  };

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e?.preventDefault();
      if (!selectedField || !selectedComparator || !selectedValue) {
        showErrorMessage('Please provide all required filter values');
        return;
      }
      const tag = `${selectedField}_${selectedComparator}_${selectedValue}`;

      onChange(tag);
      setSelectedValue('');
    },
    [selectedField, selectedComparator, selectedValue],
  );
  return (
    <div>
      <label
        htmlFor="price"
        className="block text-sm font-medium leading-6 text-gray-700 dark:text-gray-200"
      >
        Rules and Filters
      </label>
      <ul className="flex flex-row flex-wrap gap-2 mb-1">
        {(tags ?? [])?.length > 0
          ? tags?.map((tag) => {
              const isProtected = isProtectedTag(tag);
              return (
                <li
                  key={tag}
                  data-cy={dataCy ? `${dataCy}-tag` : undefined}
                  className={`flex items-center gap-2 p-1 px-2 rounded text-sm ${
                    isProtected
                      ? 'border border-green-500/60 bg-green-500/10 text-green-700 dark:text-green-400'
                      : 'bg-indigo-300 dark:bg-indigo-900 dark:text-white dark:hover:bg-indigo-800 cursor-pointer'
                  }`}
                >
                  {tag?.replace(/_/g, ' ')}
                  {isProtected ? (
                    <LockClosedIcon
                      data-cy={dataCy ? `${dataCy}-tag-lock` : undefined}
                      className="w-4 h-4 text-green-600 dark:text-green-400"
                    />
                  ) : (
                    <XIcon
                      data-cy={dataCy ? `${dataCy}-tag-remove` : undefined}
                      className="w-4 h-4 text-red-500 cursor-pointer"
                      onClick={() => {
                        onRemove(tag);
                        return false;
                      }}
                    />
                  )}
                </li>
              );
            })
          : ''}
      </ul>
      <hr className="border-1 border-gray-200 dark:border-dark-borderMid mt-2 mb-4 w-full" />
      <div className="mt-1 rounded-md shadow-sm w-full flex gap-2">
        <Select
          id="field"
          name="field"
          dataCy={dataCy ? `${dataCy}-field` : undefined}
          className="w-full lg:w-fit"
          onChange={handleFieldChange}
          value={selectedField}
          options={predicateFieldOptions}
          placeholder="---Select One---"
        />
        <Select
          id="currency"
          name="currency"
          dataCy={dataCy ? `${dataCy}-comparator` : undefined}
          className="w-full lg:w-fit"
          onChange={handleComparatorChange}
          value={selectedComparator}
          options={comparatorOptions}
          placeholder="---Select One---"
        />
      </div>
      <div className="mt-2">
        <div className="flex flex-col gap-2">
          <div className="w-full py-2 lg:w-fit">
            <FeeRuleValueInput
              field={selectedField}
              comparator={selectedComparator}
              handleValueChange={handleValueChange}
              dataCy={dataCy ? `${dataCy}-value` : undefined}
            />
          </div>
          <Button
            type="button"
            variant="solid"
            className="w-fit"
            dataCy={dataCy ? `${dataCy}-add` : undefined}
            disabled={!selectedField || !selectedComparator || !selectedValue}
            onClick={(e: React.FormEvent) => handleSubmit(e)}
          >
            Add <PlusIcon className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};
